import { useMutation, useQuery } from "@apollo/client";
import { Editor } from "@tiptap/react";
import { compact, uniq } from "lodash";
import moment from "moment";
import {
  FormEventHandler,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ImportMeetingAction,
  ImportOneononeCandidatesQueryQuery,
  ImportOneononeCandidatesQueryQueryVariables,
  ImportOneononeMeetingInput,
  ImportOneononeNotesMutationMutation,
  ImportOneononeNotesMutationMutationVariables,
  PreviewImportOneononeNotesMutationMutation,
  PreviewImportOneononeNotesMutationMutationVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Input from "@components/input/input";
import InputDatePicker from "@components/input/input-datepicker";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import CustomTextareaAutosize from "@components/textarea-autosize/textarea-autosize";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { ErrorMatchType } from "@components/use-error/use-error";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { classNames, inputThemeDefaultClassName } from "@helpers/css";
import { pluralize } from "@helpers/string";

import importOneononeCandidatesQuery from "./graphql/import-oneonone-candidates-query";
import importOneononeNotesMutation from "./graphql/import-oneonone-notes-mutation";
import previewImportOneononeNotesMutation from "./graphql/preview-import-oneonone-notes-mutation";

type Step = "paste" | "verify";

type EditableTopic = {
  key: number;
  title: string;
  notes: string;
};

type EditableMeeting = {
  key: number;
  // ISO yyyy-mm-dd date string
  date: string;
  action: ImportMeetingAction;
  existingTitle: string | null;
  existingDate: string | null;
  topics: EditableTopic[];
};

const ImportOneononeDialog = ({
  profileId,
  onClose,
  onImported,
}: {
  profileId: number;
  onClose: () => void;
  // Called after a successful import with the most-recent affected meeting id
  // (or null if nothing was imported).
  onImported?: (mostRecentMeetingId: number | null) => void;
}) => {
  const label = useLabel();
  const link = useLink();
  const oneononeLabel = label("oneonone");

  // Example date(s) for the "include a date" hint — always yesterday, so the
  // examples are concrete, recent, and unambiguously in the past.
  const exampleDate = moment().subtract(1, "day");
  const exampleNumericDate = exampleDate.format("M/D/YYYY");
  const exampleTextDate = exampleDate.format("MMM D, YYYY");

  const [step, setStep] = useState<Step>("paste");
  const [meetings, setMeetings] = useState<EditableMeeting[]>([]);

  // The paste step uses a Tiptap WYSIWYG. We keep the Editor instance in a ref
  // so we can read its plain text on Preview, and track emptiness separately so
  // the Preview button can disable itself as the user edits.
  const editorRef = useRef<Editor | null>(null);
  const [editorEmpty, setEditorEmpty] = useState(true);
  // The editor unmounts while the verify step is shown, so we mirror its content
  // (stringified Tiptap doc) here. On return to the paste step it remounts seeded
  // from this value instead of the original date seed, preserving what the user
  // pasted/typed. Null until the user's first edit, so the initial mount uses the
  // date seed.
  const [pastedValue, setPastedValue] = useState<string | null>(null);

  // Monotonic counter for stable client keys on meetings/topics. We never use
  // Math.random/Date.now so keys stay stable across re-renders and edits.
  const keyCounter = useRef(0);
  const nextKey = () => {
    keyCounter.current += 1;
    return keyCounter.current;
  };

  const { data: candidatesData, loading: candidatesLoading } = useQuery<
    ImportOneononeCandidatesQueryQuery,
    ImportOneononeCandidatesQueryQueryVariables
  >(importOneononeCandidatesQuery, {
    variables: { reportUserId: profileId },
  });

  // Seed the paste editor with one bold dated header per UNIQUE candidate date
  // (newest-first), each followed by an empty paragraph so the user types/pastes
  // notes under each date. TextareaWysiwyg reads `value` only at mount, so we
  // render the editor only once candidates have loaded (see below) and feed it
  // this stringified Tiptap doc as its initial content.
  const seedValue = useMemo(() => {
    const candidates = compact(candidatesData?.importOneononeCandidates);
    const candidateDates = uniq(
      candidates.map((c) => moment(c.startDatetime).format("MMM D, YYYY")),
    );
    // When there are no past meetings to seed from, show today's date as a single
    // example header so the user sees the expected format and where to start.
    const dates = candidateDates.length
      ? candidateDates
      : [moment().format("MMM D, YYYY")];
    // Per date: a bold date header, then two empty lines for spacing before the
    // next date. The first empty line (directly under the header) shows the grey
    // "Add notes…" placeholder via showPlaceholderAfterBoldLine below -- it's not
    // literal text, so it disappears as the user types and there's nothing to strip.
    const seeded = dates.flatMap((d) => [
      {
        type: "paragraph",
        content: [{ type: "text", marks: [{ type: "bold" }], text: d }],
      },
      { type: "paragraph" },
      { type: "paragraph" },
    ]);
    const doc = { type: "doc", content: seeded };
    return JSON.stringify(doc);
  }, [candidatesData]);

  // The editor mounts only once candidates have loaded, with `seedValue` as its
  // initial content. `onChangeValue` only fires on edits, so seed the emptiness
  // flag from the seed itself (non-empty when there is at least one candidate).
  useEffect(() => {
    if (candidatesLoading) return;
    setEditorEmpty(isEmptyValue(seedValue));
  }, [candidatesLoading, seedValue]);

  const [previewImportOneononeNotes, { loading: previewing }] = useMutation<
    PreviewImportOneononeNotesMutationMutation,
    PreviewImportOneononeNotesMutationMutationVariables
  >(previewImportOneononeNotesMutation);

  const [importOneononeNotes, { loading: committing }] = useMutation<
    ImportOneononeNotesMutationMutation,
    ImportOneononeNotesMutationMutationVariables
  >(importOneononeNotesMutation, {
    // Best-effort refresh of the profile 1-on-1 list after an import.
    refetchQueries: ["getProfileOneononesQuery"],
  });

  // Expected, user-facing validation errors from these mutations: each is shown
  // as a toast and skips Sentry. `match` is a stable substring of the backend
  // message; `description` is the text we actually show. Anything NOT listed
  // here falls through to the global handler's generic toast + Sentry capture,
  // so genuinely unexpected failures (bugs) are still reported.
  const previewErrorMatches: ErrorMatchType[] = [
    {
      match: "Notes cannot be empty",
      description: `Paste your ${oneononeLabel} notes before previewing.`,
    },
    {
      match: "date with the year",
      description: `Include a date with the year in your notes so we can match them to ${oneononeLabel}s.`,
    },
    {
      match: "Add notes under at least one past date",
      description:
        "Add notes under at least one past date so there's something to import.",
    },
    {
      match: "in the future",
      description: `You can only import notes for past ${oneononeLabel}s.`,
    },
    {
      match: "You must be the manager",
      description: `You must be this person's manager to import a past ${oneononeLabel}.`,
    },
  ].map((m) => ({
    ...m,
    title: `The ${oneononeLabel} notes could not be previewed.`,
  }));

  const importErrorMatches: ErrorMatchType[] = [
    {
      match: "Include at least one meeting",
      description: `Include at least one ${oneononeLabel} to import.`,
    },
    {
      match: "You must be the manager",
      description: `You must be this person's manager to import a past ${oneononeLabel}.`,
    },
  ].map((m) => ({
    ...m,
    title: `The ${oneononeLabel} notes could not be imported.`,
  }));

  const pasteInvalid = editorEmpty || previewing;

  const handleSubmitForm: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClickPreview = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (pasteInvalid) return;

    // Serialize the editor to plain text. Bold marks (the seeded date headers)
    // vanish in getText(), so date lines stay plain and parse on the backend.
    const notesText = editorRef.current?.getText() ?? "";
    if (notesText.trim() === "") return;

    previewImportOneononeNotes({
      variables: {
        reportUserId: profileId,
        notes: notesText,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      // Known validation errors map to a toast and skip Sentry; unexpected
      // errors fall through to the global handler (generic toast + Sentry).
      context: { errorMatches: previewErrorMatches },
      onCompleted: (data) => {
        const plan = compact(data.previewImportOneononeNotes?.plan);
        const editable: EditableMeeting[] = plan.map((meeting) => ({
          key: nextKey(),
          date: moment(meeting.date).format("YYYY-MM-DD"),
          action: meeting.action,
          existingTitle: meeting.existingMeeting?.title ?? null,
          existingDate: meeting.existingMeeting?.startDatetime ?? null,
          topics: compact(meeting.topics).map((topic) => ({
            key: nextKey(),
            title: topic.title ?? "",
            notes: topic.notes ?? "",
          })),
        }));
        setMeetings(editable);
        setStep("verify");
      },
    });
  };

  // Every edit below replaces a single meeting card (matched by key); share the walk.
  const mapMeeting = (
    key: number,
    fn: (m: EditableMeeting) => EditableMeeting,
  ) => setMeetings((prev) => prev.map((m) => (m.key === key ? fn(m) : m)));

  const updateMeeting = (key: number, patch: Partial<EditableMeeting>) =>
    mapMeeting(key, (m) => ({ ...m, ...patch }));

  const removeMeeting = (key: number) =>
    setMeetings((prev) => prev.filter((m) => m.key !== key));

  const updateTopic = (
    meetingKey: number,
    topicKey: number,
    patch: Partial<EditableTopic>,
  ) =>
    mapMeeting(meetingKey, (m) => ({
      ...m,
      topics: m.topics.map((t) =>
        t.key === topicKey ? { ...t, ...patch } : t,
      ),
    }));

  const removeTopic = (meetingKey: number, topicKey: number) =>
    mapMeeting(meetingKey, (m) => ({
      ...m,
      topics: m.topics.filter((t) => t.key !== topicKey),
    }));

  const addTopic = (meetingKey: number) =>
    mapMeeting(meetingKey, (m) => ({
      ...m,
      topics: [...m.topics, { key: nextKey(), title: "", notes: "" }],
    }));

  // The commit payload: every remaining card with at least one topic that has a non-empty title
  // or notes. Memoized over `meetings` so editing a topic doesn't rebuild it on every render, and
  // so the confirm handler reuses the same array.
  const meetingsPayload = useMemo<ImportOneononeMeetingInput[]>(
    () =>
      meetings
        .map((m) => ({
          date: m.date,
          topics: m.topics
            .filter((t) => t.title.trim() || t.notes.trim())
            .map((t) => ({ title: t.title.trim() || "Notes", notes: t.notes })),
        }))
        .filter((m) => m.topics.length > 0),
    [meetings],
  );

  const confirmInvalid = committing || meetingsPayload.length === 0;

  const handleClickConfirm = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (confirmInvalid) return;

    importOneononeNotes({
      variables: {
        reportUserId: profileId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        meetings: meetingsPayload,
      },
      // Known validation errors map to a toast and skip Sentry; unexpected
      // errors fall through to the global handler (generic toast + Sentry).
      context: { errorMatches: importErrorMatches },
      onCompleted: (data) => {
        const result = data.importOneononeNotes;
        const affected = compact(result?.meetings);
        const skipped = compact(result?.skippedDates);

        const count = affected.length;
        // Skipped dates are future-dated (the only case the commit skips); the verify-step date
        // picker blocks future dates, so this is a defensive note that rarely shows.
        const skippedNote = skipped.length
          ? ` ${skipped.length} future ${pluralize("date", skipped.length)} skipped.`
          : "";
        successNotificationVar({
          title: `Imported ${count} ${pluralize(oneononeLabel, count)}.`,
          description: skippedNote.trim() || undefined,
        });

        const mostRecentId = count ? affected[0].id : null;
        onImported?.(mostRecentId);
        onClose();
        // affected is newest-first, so [0] is the most recent.
        if (mostRecentId !== null) {
          link.redirect(`/profile/${profileId}/oneonone/${mostRecentId}`);
        }
      },
    });
  };

  const renderActionBadge = (meeting: EditableMeeting) => {
    if (meeting.action === ImportMeetingAction.Update) {
      const existing = meeting.existingTitle || `existing ${oneononeLabel}`;
      const when = meeting.existingDate
        ? ` (${moment(meeting.existingDate).format("MMM D, YYYY")})`
        : "";
      return (
        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
          Update existing: {existing}
          {when}
        </span>
      );
    }
    return (
      <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
        New {oneononeLabel}
      </span>
    );
  };

  return (
    <Modal onClose={onClose} alignedTop>
      <form
        className="flex max-h-[85vh] flex-col gap-6 p-6"
        onSubmit={handleSubmitForm}
      >
        <ModalTitle onClose={onClose}>
          Import {label("oneonone", { capitalize: true })} notes
        </ModalTitle>

        {step === "paste" ? (
          <>
            <div className="flex flex-col gap-2">
              <p className="text-gray-500 text-sm">
                Paste the content of your {oneononeLabel} notes below.
                We&apos;ll suggest how to split them into meetings so you can
                review before importing.
              </p>
              <p className="text-gray-400 text-xs">
                Include a date with the year above each section (e.g. &quot;
                {exampleNumericDate}&quot; or &quot;{exampleTextDate}&quot;) so
                we can match them to meetings.
              </p>
              <p className="text-gray-500 text-sm">
                Heads up: imported notes are added to the {oneononeLabel} you
                share, so{" "}
                <span className="font-medium">
                  your direct report will be able to see them
                </span>
                . Keep anything you want to stay private in your Private Notes
                instead.
              </p>
            </div>

            {/* The editor is a fixed-height box (sized to fill most of the
                modal) that scrolls its content internally, so its border is
                never clipped no matter how many dates are seeded. */}
            <div className="flex flex-col gap-2">
              {candidatesLoading ? (
                <div className="flex h-[55vh] items-center justify-center">
                  <Loading />
                </div>
              ) : (
                // The editor reads `value` only at mount, so we render it only
                // once candidates have loaded — guaranteeing the initial content
                // is present. It seeds from `pastedValue` (what the user last
                // typed) when returning from the verify step, falling back to the
                // date seed on first open. TextareaWysiwyg supplies its own input
                // border/rounding when editable; h-[55vh] + the overflow on
                // .ProseMirror keep it a bounded box that scrolls inside its
                // border rather than overflowing and getting clipped.
                <div aria-label="notes" className="flex flex-col">
                  <TextareaWysiwyg
                    ref={editorRef}
                    value={pastedValue ?? seedValue}
                    editable
                    autofocus
                    // Grey hint shown on the empty line directly under each date header,
                    // so the user types over nothing.
                    placeholder="Add notes…"
                    showPlaceholderAfterBoldLine
                    className="h-[55vh] overflow-y-auto"
                    onChangeValue={(value) => {
                      setPastedValue(value);
                      setEditorEmpty(isEmptyValue(value));
                    }}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-4">
              <Button
                disabled={previewing}
                onClick={onClose}
                type="button"
                text="Cancel"
                size={ButtonSize.large}
              />
              <Button
                disabled={pasteInvalid}
                type="submit"
                theme={buttonTheme.primary}
                onClick={handleClickPreview}
                className="flex items-center gap-2"
                size={ButtonSize.large}
              >
                {previewing ? "Previewing" : "Preview"}
                {previewing && <Loading mini size="4" color="white" />}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-gray-500 text-sm">
              Review and edit the {oneononeLabel}s below before importing. You
              can change dates, edit topics, and remove anything you don&apos;t
              want. Once imported, these notes will be visible to your direct
              report on the {oneononeLabel} you share.
            </p>

            <div
              aria-label="import-verify"
              className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1"
            >
              {meetings.map((meeting) => {
                return (
                  <div
                    key={meeting.key}
                    aria-label="import-meeting"
                    className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4"
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <InputDatePicker
                          date={moment(meeting.date).toDate()}
                          maxDate={new Date()}
                          isClearable={false}
                          onChangeDate={(d) => {
                            if (!d) return;
                            updateMeeting(meeting.key, {
                              date: moment(d).format("YYYY-MM-DD"),
                            });
                          }}
                        />
                        <Button
                          type="button"
                          theme={buttonTheme.iconGray}
                          size={ButtonSize.large}
                          square
                          aria-label="remove-meeting"
                          onClick={() => removeMeeting(meeting.key)}
                        >
                          <TopicflowIcon type="trash" className="h-4 w-4" />
                        </Button>
                      </div>
                      {/* Action / existing-meeting title stays below the date row. */}
                      {renderActionBadge(meeting)}
                    </div>

                    <div className="flex flex-col gap-3">
                      {meeting.topics.map((topic) => (
                        <div
                          key={topic.key}
                          aria-label="import-topic"
                          className="flex flex-col gap-1 rounded-md border border-gray-100 bg-white p-2"
                        >
                          <div className="flex items-start gap-2">
                            <Input
                              aria-label="topic title"
                              value={topic.title}
                              placeholder="Topic title"
                              className="flex-1"
                              onChange={(e) =>
                                updateTopic(meeting.key, topic.key, {
                                  title: e.target.value,
                                })
                              }
                            />
                            <Button
                              type="button"
                              theme={buttonTheme.iconGray}
                              size={ButtonSize.large}
                              square
                              aria-label="remove-topic"
                              onClick={() =>
                                removeTopic(meeting.key, topic.key)
                              }
                            >
                              <TopicflowIcon type="trash" className="h-4 w-4" />
                            </Button>
                          </div>
                          <CustomTextareaAutosize
                            aria-label="topic notes"
                            value={topic.notes}
                            minRows={2}
                            placeholder="Notes"
                            className={classNames(
                              inputThemeDefaultClassName,
                              "min-h-[3rem] py-2",
                            )}
                            onChange={(e) =>
                              updateTopic(meeting.key, topic.key, {
                                notes: e.target.value,
                              })
                            }
                          />
                        </div>
                      ))}
                      <div>
                        <Button
                          type="button"
                          theme={buttonTheme.text}
                          size={ButtonSize.small}
                          aria-label="add-topic"
                          className="flex items-center gap-1"
                          onClick={() => addTopic(meeting.key)}
                        >
                          <TopicflowIcon type="plus" className="h-4 w-4" />
                          Add topic
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between gap-4">
              <Button
                disabled={committing}
                onClick={() => setStep("paste")}
                type="button"
                size={ButtonSize.large}
              >
                Back
              </Button>
              <Button
                disabled={confirmInvalid}
                type="submit"
                theme={buttonTheme.primary}
                onClick={handleClickConfirm}
                className="flex items-center gap-2"
                size={ButtonSize.large}
              >
                {committing ? "Importing" : "Confirm import"}
                {committing && <Loading mini size="4" color="white" />}
              </Button>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};

export default ImportOneononeDialog;
